import React, { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, Send, MapPin, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactSection() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [nlLoading, setNlLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Merci de remplir nom, email et message.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      toast.success(data.message);
      setForm({
        name: "",
        email: "",
        company: "",
        phone: "",
        subject: "",
        message: "",
      });
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Erreur lors de l'envoi. Réessayez."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleNewsletter = async (e) => {
    e.preventDefault();
    if (!newsletterEmail) {
      toast.error("Veuillez saisir votre email.");
      return;
    }
    setNlLoading(true);
    try {
      const { data } = await axios.post(`${API}/newsletter/subscribe`, {
        email: newsletterEmail,
      });
      toast.success(data.message);
      setNewsletterEmail("");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Inscription impossible. Réessayez."
      );
    } finally {
      setNlLoading(false);
    }
  };

  const inputClass =
    "w-full bg-[#080F0B] border border-[#1A2E22] focus:border-emerald-500/60 rounded-xl px-4 py-3 text-white placeholder:text-gray-600 outline-none transition-colors";

  return (
    <section id="contact" className="relative py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-12">
          {/* Left column */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-5"
          >
            <div className="font-mono text-xs text-emerald-400 mb-4 tracking-widest">
              // CONTACT
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold leading-[1.05] tracking-tight">
              Parlons de votre
              <br />
              <span className="italic text-emerald-400">prochain défi.</span>
            </h2>
            <p className="mt-6 text-gray-400 leading-relaxed max-w-md">
              Audit, accompagnement ISO 27001, mise en place de SOC, réponse à
              incident — notre équipe revient vers vous sous 24h.
            </p>

            <div className="mt-10 space-y-4">
              <a
                href="mailto:contact@baobsec.com"
                data-testid="contact-email-link"
                className="flex items-center gap-4 p-5 rounded-2xl bg-[#0C1410] border border-[#1A2E22] hover:border-emerald-500/40 transition-colors group"
              >
                <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400">
                  <Mail className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs text-gray-500 font-mono">EMAIL</div>
                  <div className="text-white group-hover:text-emerald-400 transition-colors">
                    contact@baobsec.com
                  </div>
                </div>
              </a>

              <a
                href="tel:+33652353418"
                data-testid="contact-phone-link"
                className="flex items-center gap-4 p-5 rounded-2xl bg-[#0C1410] border border-[#1A2E22] hover:border-emerald-500/40 transition-colors group"
              >
                <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400">
                  <Phone className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs text-gray-500 font-mono">TÉLÉPHONE</div>
                  <div className="text-white group-hover:text-emerald-400 transition-colors">
                    +33 6 52 35 34 18
                  </div>
                </div>
              </a>

              <div className="flex items-center gap-4 p-5 rounded-2xl bg-[#0C1410] border border-[#1A2E22]">
                <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400">
                  <MapPin className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs text-gray-500 font-mono">PRÉSENCE</div>
                  <div className="text-white">France 🇫🇷 · Sénégal 🇸🇳</div>
                  <div className="text-[11px] text-gray-500 mt-1">
                    Interventions à distance · Afrique de l'Ouest & Europe
                  </div>
                </div>
              </div>
            </div>

            {/* Newsletter */}
            <form
              onSubmit={handleNewsletter}
              data-testid="newsletter-form"
              className="mt-10 p-6 rounded-2xl bg-gradient-to-br from-emerald-500/10 to-transparent border border-emerald-500/25"
            >
              <div className="font-mono text-xs text-emerald-400 mb-3 tracking-widest">
                // NEWSLETTER
              </div>
              <h4 className="font-display text-xl font-semibold mb-2">
                Veille cybersécurité mensuelle
              </h4>
              <p className="text-sm text-gray-400 mb-4">
                Recevez nos analyses ISO 27001, alertes NIS2 et retours
                d'expérience SOC.
              </p>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  data-testid="newsletter-email-input"
                  type="email"
                  required
                  placeholder="votre@email.com"
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                  className={inputClass + " flex-1"}
                />
                <button
                  type="submit"
                  disabled={nlLoading}
                  data-testid="newsletter-submit-button"
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-black font-semibold transition-all disabled:opacity-50"
                >
                  {nlLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    "S'inscrire"
                  )}
                </button>
              </div>
            </form>
          </motion.div>

          {/* Right column - Form */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-7"
          >
            <form
              onSubmit={handleSubmit}
              data-testid="contact-form"
              className="relative p-7 md:p-10 rounded-3xl bg-[#0C1410] border border-[#1A2E22]"
            >
              <div className="absolute -top-3 left-7 px-3 py-1 rounded-full bg-emerald-500 text-black text-xs font-mono font-bold">
                / GET IN TOUCH
              </div>

              <div className="grid sm:grid-cols-2 gap-5 mt-3">
                <div>
                  <label className="block text-xs text-gray-500 font-mono mb-2">
                    NOM COMPLET *
                  </label>
                  <input
                    data-testid="contact-name-input"
                    required
                    type="text"
                    placeholder="Jean Dupont"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 font-mono mb-2">
                    EMAIL *
                  </label>
                  <input
                    data-testid="contact-email-input"
                    required
                    type="email"
                    placeholder="jean@entreprise.com"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 font-mono mb-2">
                    ENTREPRISE
                  </label>
                  <input
                    data-testid="contact-company-input"
                    type="text"
                    placeholder="Acme Inc."
                    value={form.company}
                    onChange={(e) =>
                      setForm({ ...form, company: e.target.value })
                    }
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 font-mono mb-2">
                    TÉLÉPHONE
                  </label>
                  <input
                    data-testid="contact-phone-input"
                    type="tel"
                    placeholder="+33 6 ..."
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className={inputClass}
                  />
                </div>
              </div>

              <div className="mt-5">
                <label className="block text-xs text-gray-500 font-mono mb-2">
                  SUJET
                </label>
                <select
                  data-testid="contact-subject-select"
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  className={inputClass}
                >
                  <option value="">— Sélectionnez un sujet —</option>
                  <option value="Audit & analyse de risques">
                    Audit & analyse de risques
                  </option>
                  <option value="Certification ISO 27001">
                    Certification ISO 27001
                  </option>
                  <option value="Mise en place SOC / SIEM / EDR">
                    Mise en place SOC / SIEM / EDR
                  </option>
                  <option value="Réponse à incident">Réponse à incident</option>
                  <option value="Conformité RGPD / NIS2">
                    Conformité RGPD / NIS2
                  </option>
                  <option value="Autre">Autre</option>
                </select>
              </div>

              <div className="mt-5">
                <label className="block text-xs text-gray-500 font-mono mb-2">
                  VOTRE MESSAGE *
                </label>
                <textarea
                  data-testid="contact-message-input"
                  required
                  rows={6}
                  placeholder="Décrivez votre contexte et vos enjeux..."
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className={inputClass + " resize-none"}
                />
              </div>

              <div className="mt-7 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <p className="text-xs text-gray-500">
                  En soumettant ce formulaire, vous acceptez d'être recontacté par
                  BaobSec dans le cadre de votre demande.
                </p>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-form-submit-button"
                  className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black font-semibold transition-all hover:-translate-y-[2px] disabled:opacity-50 disabled:hover:translate-y-0 glow-emerald"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      Envoyer le message
                      <Send className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
